/**
 * The corpus a row came from — Epic 6 design §1.1.
 *
 * politsei.ee publishes two daily series this project reads: the police digest
 * ("Politseis registreeritud sündmused", category 2) and Liiklusinfo (category 3). They describe
 * overlapping events in incompatible vocabularies, so **no query, chart or badge ever mixes them**.
 * That rule is only enforceable if "which series" is one value with one spelling, so it lives here.
 *
 * Three properties of this module are load-bearing:
 *
 *  - **Semantic values, not the site's category id.** `'digest' | 'traffic'`, never `'2' | '3'`.
 *    The numeric id is a scrape-layer fact about one CMS's URL space; it survives in exactly one
 *    place, the series registry, which is also the only thing that knows the index URL.
 *  - **No `'ALL'`.** Two series describing the same events cannot be summed, so the value that
 *    would ask for the sum does not exist and therefore cannot be passed. Same reasoning, and same
 *    shape, as `daily_aggregates.scope`.
 *  - **This is where the closed set is enforced.** The schema stores `source` as plain TEXT with
 *    `DEFAULT 'digest'` and no `CHECK` constraint (§1.1): two values written from a registry are
 *    better policed in TypeScript, where the error names the offending call site, than by a
 *    constraint that names a table.
 *
 * Top-level, beside `config.ts` and `logger.ts`, because every layer needs it — the scraper walks a
 * series, the parser stamps it, the extractor branches on it through `extract/registry.ts`, and the
 * API filters on it. The extractor's branch is the load-bearing one: each series has its own prompt
 * under its own globally-unique version string, which is the only thing separating the two corpora
 * in a cache whose key does not carry `source` and whose hashes they demonstrably share.
 */

/** The two series, in the order they joined the corpus. */
export const SOURCES = ['digest', 'traffic'] as const;

/** Which of the two daily series a post, segment, incident or tally belongs to. */
export type Source = (typeof SOURCES)[number];

/**
 * What a caller that does not name a series means.
 *
 * The digest is the corpus this project started as: every stored row predates the column, every
 * existing URL and bookmark was written against it, and migration 006's `DEFAULT 'digest'` is the
 * same statement in DDL. Keeping the default in one exported constant is what lets the HTTP layer
 * and the schema agree by construction rather than by coincidence.
 */
export const DEFAULT_SOURCE: Source = 'digest';

/** Whether a value read from the database or a query string is one of the two known series. */
export function isSource(value: unknown): value is Source {
  return typeof value === 'string' && (SOURCES as readonly string[]).includes(value);
}
